import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import ImageScroller from '../../components/imagescroller';
import { buildStandCourseSlides, getStandCourseFacts, getStandMagnumFacts } from '../../lib/conventionCourse';
import { printStandPoster } from '../../lib/conventionStandPoster';

function courseImage(img) {
    if (!img || typeof img !== 'object') {
        return undefined;
    }
    return img.s3XLargeLink || img.s3LargeLink || img.s3MediumLink || img.s3Link;
}

const Fact = ({ icon, label, value }) => (
    <div class='col-6 col-md-4'>
        <div class='h-100 rounded-lg border bg-body-secondary px-3 py-2'>
            <div class='small text-body-secondary'>
                <i class={`fa-duotone ${icon} me-1`} />{label}
            </div>
            <div class='fw-semibold mt-1'>{value}</div>
        </div>
    </div>
);

// Magnum-specific shooting position / time set on the stand
// (magnumShootingPosition / magnumShootingTime). Nothing when neither is set.
const MagnumBox = ({ facts }) => {
    if (!facts) {
        return null;
    }
    return (
        <div class='rounded-lg border px-3 py-3 mt-2'>
            <div class='small text-body-secondary mb-1'>
                <i class='fa-duotone fa-bullseye-pointer me-1' />
                <Text id='convention.course-magnum'>Magnum</Text>
            </div>
            <div class='fw-semibold'>
                {facts.shootingPosition}
                {facts.shootingPosition && facts.shootingTime != null && ' · '}
                {facts.shootingTime != null && (
                    <>{facts.shootingTime} <Text id='convention.course-seconds'>seconds</Text></>
                )}
            </div>
            {facts.notes && <div class='mt-1' style='white-space: pre-wrap;'>{facts.notes}</div>}
        </div>
    );
};

@observer
class ConventionCourseDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            activeSlide: 0,
        };
        this.imageScroller = null;
    }

    selectSlide = (activeSlide) => {
        this.setState({ activeSlide });
    }

    showPreviousSlide = () => {
        if (this.imageScroller) {
            this.imageScroller.scrollLeft();
        }
    }

    showNextSlide = () => {
        if (this.imageScroller) {
            this.imageScroller.scrollRight();
        }
    }

    render() {
        const { conventionStore } = this.props.stores;
        const convention = conventionStore.convention || {};
        const slides = buildStandCourseSlides(convention);
        const activeSlide = Math.min(this.state.activeSlide, Math.max(slides.length - 1, 0));
        const currentSlide = slides[activeSlide];

        if (!currentSlide) {
            return (
                <div class='h-100 d-flex flex-column align-items-center justify-content-center px-3 text-center'>
                    <i class='fa-duotone fa-route fa-3x text-body-secondary mb-3' />
                    <Text id='convention.course-empty'>No stand images have been added yet.</Text>
                </div>
            );
        }

        const facts = getStandCourseFacts(currentSlide.stand);
        const magnumFacts = getStandMagnumFacts(currentSlide.stand);
        const images = slides.map(slide => slide.image);

        return (
            <div class='h-100 d-flex flex-column bg-body text-body' style='padding-top: 3.25rem;'>
                <div class='flex-grow-1 overflow-auto px-3 pb-4'>
                    <div class='text-center mb-3'>
                        <div class='text-body-secondary'>{convention.title}</div>
                        <h4 class='mb-1'>
                            <i class='fa-duotone fa-route me-2 text-primary' />
                            <Text id='convention.course-title'>View the course</Text>
                        </h4>
                        <small class='text-body-secondary'>
                            <Text id='convention.course-swipe-help'>Swipe or use the buttons to see every stand.</Text>
                        </small>
                    </div>

                    <ImageScroller
                        ref={component => this.imageScroller = component}
                        images={images}
                        stores={this.props.stores}
                        callback={this.selectSlide}
                        showImg={courseImage}
                        showLargeImage={courseImage}
                        showImageInfo={false}
                        classNames='bg-body-secondary overflow-hidden'
                        maxHeight='52vh'
                        minHeight='220px'
                        imageContainerStyles='background: transparent; object-fit: contain;'
                    />

                    <div class='d-flex flex-row align-items-center justify-content-between gap-2 mt-3'>
                        <button
                            type='button'
                            class='btn btn-outline-primary rounded-pill'
                            disabled={activeSlide === 0}
                            onClick={this.showPreviousSlide}
                        >
                            <i class='fa-solid fa-chevron-left me-1' />
                            <Text id='convention.course-previous'>Previous</Text>
                        </button>
                        <div class='text-center'>
                            <h4 class='mb-0'>
                                <Text id='convention.course-stand'>Stand</Text> {currentSlide.standNumber}
                            </h4>
                            <small class='text-body-secondary'>{activeSlide + 1} / {slides.length}</small>
                        </div>
                        <button
                            type='button'
                            class='btn btn-outline-primary rounded-pill'
                            disabled={activeSlide === slides.length - 1}
                            onClick={this.showNextSlide}
                        >
                            <Text id='convention.course-next'>Next</Text>
                            <i class='fa-solid fa-chevron-right ms-1' />
                        </button>
                    </div>

                    <div class='d-flex justify-content-center mt-3'>
                        <button
                            type='button'
                            class='btn btn-outline-primary rounded-pill px-4'
                            onClick={() => printStandPoster({ convention, stand: currentSlide.standNumber })}
                        >
                            <i class='fa-duotone fa-sign-hanging me-2' />
                            <Text id='convention.standplass-poster-print'>Print poster</Text>
                        </button>
                    </div>

                    <div class='row g-2 mt-3'>
                        {facts.ipsc && (<>
                            <Fact icon='fa-bullseye' label={<Text id='ipsc.paper-targets'>Paper targets</Text>} value={`${facts.ipsc.paper} × ${facts.ipsc.hitsPerPaper}`} />
                            <Fact icon='fa-circle-dot' label={<Text id='ipsc.steel'>Steel</Text>} value={facts.ipsc.poppers + facts.ipsc.plates} />
                            <Fact icon='fa-ban' label={<Text id='ipsc.no-shoots'>No-shoots</Text>} value={facts.ipsc.noShoots} />
                            <Fact icon='fa-list-ol' label={<Text id='ipsc.min-rounds'>Min rounds</Text>} value={facts.ipsc.minRounds} />
                            <Fact icon='fa-star' label={<Text id='ipsc.max-points'>Max points</Text>} value={facts.ipscMax} />
                        </>)}
                        {facts.scoreGroups != null && (
                            <Fact
                                icon='fa-layer-group'
                                label={<Text id='convention.course-target-groups'>Target groups</Text>}
                                value={facts.scoreGroups}
                            />
                        )}
                        {facts.scoreTargets != null && (
                            <Fact
                                icon='fa-bullseye'
                                label={<Text id='convention.course-figures'>Figures</Text>}
                                value={facts.scoreTargets}
                            />
                        )}
                        {facts.distance && (
                            <Fact
                                icon='fa-ruler-horizontal'
                                label={<Text id='convention.course-distance'>Distance</Text>}
                                value={facts.distance}
                            />
                        )}
                        {facts.shootingTime != null && (
                            <Fact
                                icon='fa-clock'
                                label={<Text id='convention.course-shooting-time'>Shooting time</Text>}
                                value={<>{facts.shootingTime} <Text id='convention.course-seconds'>seconds</Text></>}
                            />
                        )}
                        {facts.scoreHits != null && (
                            <Fact
                                icon='fa-crosshairs'
                                label={<Text id='convention.course-max-hits'>Max hits</Text>}
                                value={facts.scoreHits}
                            />
                        )}
                        {facts.scoreInner != null && (
                            <Fact
                                icon='fa-bullseye-arrow'
                                label={<Text id='convention.course-zones'>Zones</Text>}
                                value={facts.scoreInner}
                            />
                        )}
                    </div>

                    {facts.ipscBrief && (
                        <div class='rounded-lg border px-3 py-3 mt-2'>
                            <div class='small text-body-secondary mb-1'>
                                <i class='fa-duotone fa-clipboard-list me-1' />
                                <Text id='ipsc.stage-brief'>Stage brief</Text>
                            </div>
                            <div class='fw-semibold' style='white-space: pre-wrap;'>{facts.ipscBrief}</div>
                        </div>
                    )}
                    {facts.shootingPosition && (
                        <div class='rounded-lg border px-3 py-3 mt-2'>
                            <div class='small text-body-secondary mb-1'>
                                <i class='fa-duotone fa-person-rifle me-1' />
                                <Text id='convention.course-position'>Position</Text>
                            </div>
                            <div class='fw-semibold'>{facts.shootingPosition}</div>
                        </div>
                    )}

                    {facts.notes && (
                        <div class='rounded-lg border px-3 py-3 mt-2'>
                            <div class='small text-body-secondary mb-1'>
                                <i class='fa-duotone fa-note me-1' />
                                <Text id='convention.course-notes'>Notes</Text>
                            </div>
                            <div style='white-space: pre-wrap;'>{facts.notes}</div>
                        </div>
                    )}

                    <MagnumBox facts={magnumFacts} />
                </div>
            </div>
        );
    }
}

export default ConventionCourseDrawer;
